<template>
  <b-card
    v-if="data"
    no-body
    class="card-tickettrend-budget h-100 overflow-visible"
  >
    <b-row class="mx-2">
      <b-col
        md="12"
        class="tickettrend-report-wrapper"
      >
        <div
          class="d-flex flex-wrap justify-content-between align-items-center mb-3 mt-2 position-relative"
          style="z-index: 10; gap: 10px;"
        >
          <h4
            class="card-title mb-50 mb-sm-0 chart-title d-flex align-items-center flex-wrap"
          >
            <span class="mr-1">{{ $t('Ticket By Trend') }}</span>
            <tooltip-help :text="$t('Monthly Ticket Volume Trend')" />
          </h4>
          <div class="d-flex align-items-center flex-wrap gap-2">
            <b-button
              v-if="showRefreshButton"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              size="sm"
              variant="outline-primary"
              class="widgetrefresh-button d-flex align-items-center justify-content-center shadow-custom"
              :style="cardBorder"
              :disabled="!canRefresh"
              @click.stop="refreshWidget"
            >
              <feather-icon
                icon="RefreshCwIcon"
                class="mr-50 refresh-icon"
              />
              <span class="align-middle refresh-span" />
              <time-ago
                class="timeAgo"
                :datetime="lastRefreshTime ? new Date(parseInt(lastRefreshTime, 10)).toISOString() : lastUpdatedAt"
                refresh
                locale="en"
                :long="longString"
                :style="{color: '#1C1F32'}"
              />
            </b-button>
          </div>
        </div>

        <!-- chart -->
        <vue-apex-charts
          id="tickettrend-report-chart"
          height="300"
          :options="chartOptions"
          :series="data.series"
        />
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import Ripple from 'vue-ripple-directive'
import { TimeAgo } from 'vue2-timeago'
import RefreshMixin from '@/@core/mixins/ui/refresh'
import TooltipHelp from '../Dashboard/TooltipHelp.vue'

export default {
  components: {
    VueApexCharts,
    BCard,
    BRow,
    BCol,
    TimeAgo,
    BButton,
    TooltipHelp,
  },
  directives: {
    Ripple,
  },
  mixins: [RefreshMixin],
  props: {
    data: {
      type: Object,
      default: () => {
      },
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
    showRefreshButton: {
      type: Boolean,
      default: true, // or false, depending on your default preference
    },
  },
  data() {
    return {
      longString: true,
      lastRefreshTime: (JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}).Trend || 0,
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    chartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: true },
        },
        colors: ['#28C76F'],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 3,
          curve: 'smooth',
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'vertical',
            shadeIntensity: 0.5,
            gradientToColors: ['#28C76F'],
            inverseColors: false,
            opacityFrom: 0.5,
            opacityTo: 0.1,
            stops: [0, 100],
          },
        },
        grid: {
          borderColor: '#ebe9f1',
          strokeDashArray: 5,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: {
            top: -20,
            right: 40,
            left: 20,
          },
        },
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          theme: 'dark',
          custom({
            series, seriesIndex, dataPointIndex,
          }) {
            let sIdx = seriesIndex
            if (sIdx === undefined || sIdx === -1) sIdx = 0
            const value = series[sIdx] ? series[sIdx][dataPointIndex] : null

            if (value === undefined || value === null) return ''

            return `
              <div style="
                background: #28C76F;
                color: #fff;
                padding: 4px 12px;
                border-radius: 12px;
                font-size: 13px;
                font-weight: 600;
                position: relative;
                box-shadow: 0 4px 8px rgba(0,0,0,0.2);
                pointer-events: none;
              ">
                ${value.toLocaleString()}
              </div>`
          },
        },
        markers: {
          size: 5,
          colors: ['#28C76F'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { size: 7 },
        },
        xaxis: {
          categories: this.data?.categories?.lables || [],
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false },
          labels: {
            show: true,
            style: {
              fontSize: '11px',
              colors: '#b9b9c3',
              fontWeight: 500,
            },
          },
        },
        yaxis: {
          labels: {
            formatter: value => Math.round(value).toLocaleString(),
            style: {
              colors: '#b9b9c3',
              fontSize: '11px',
            },
          },
        },
      }
    },
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
        opacity: this.canRefresh ? 1 : 0.5,
      }
    },
    canRefresh() {
      const diff = this.now - parseInt(this.lastRefreshTime, 10)
      return diff >= 60000
    },
  },
  created() {
    this.timer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  mounted() {
    const timestamps = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
    if (!timestamps.Trend) {
      this.refreshWidget()
    }
  },
  methods: {
    refreshWidget() {
      if (!this.canRefresh) return
      const timestamp = Date.now()
      this.lastRefreshTime = timestamp
      const timestampsObj = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
      timestampsObj.Trend = timestamp
      localStorage.setItem('ticketDashboardLastUpdatedAt', JSON.stringify(timestampsObj))
      this.$emit('refresh', this.queue)
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';
::v-deep .apexcharts-tooltip {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
.refresh-icon {
  color: #1C1F32;
}
.widgetrefresh-button {
  cursor: pointer;
}
.overflow-visible {
  overflow: visible !important;
}
</style>
<style lang="scss">
.blue-tooltip .tooltip-inner {
  background-color: #0094FF !important;
  color: #fff !important;
  font-weight: 500;
  border-radius: 8px;
  padding: 6px 12px;
}
.blue-tooltip .arrow::before {
  border-top-color: #0094FF !important;
  border-bottom-color: #0094FF !important;
}
</style>
